import { ApiError, api, unwrap } from "@/shared/api/client";
import { observeFrontendMetric } from "@/shared/observability/performance";
import {
  isNative,
  nativeClearOfflineKey,
  nativeDecryptOffline,
  nativeEncryptOffline,
} from "@/shared/platform/native";

const DATABASE_NAME = "exognitos-offline-v1";
const DATABASE_VERSION = 1;
const OUTBOX_EVENT = "exognitos:offline-outbox-change";
const MAX_CAPTURE_ITEMS = 200;
const MAX_ENCRYPTED_BYTES = 1_048_576;
const CAPTURE_SCHEMA_VERSION = 1;
const ALLOWED_PROJECTIONS = new Set(["inbox_count"]);

type OutboxState = "pending" | "conflict" | "rejected" | "corrupted";

type StoredKey = {
  accountId: string;
  key?: CryptoKey;
  native?: true;
  createdAt: number;
};

type StoredCapture = {
  id: string;
  accountId: string;
  operation: "memory.capture.create";
  schemaVersion: 1;
  idempotencyKey: string;
  iv: ArrayBuffer;
  ciphertext: ArrayBuffer;
  createdAt: number;
  state: OutboxState;
  attempts: number;
  lastErrorCode: string | null;
};

type StoredProjection = {
  id: string;
  accountId: string;
  name: string;
  schemaVersion: 1;
  schemaEpoch: number;
  resetToken: string | null;
  capturedAt: number;
  iv: ArrayBuffer;
  ciphertext: ArrayBuffer;
};

export type OfflineCaptureDraft = {
  id: string;
  body: string;
  idempotencyKey: string;
};

export type OfflineDraftReview = {
  id: string;
  body: string | null;
  createdAt: number;
  state: OutboxState;
  errorCode: string | null;
};

export type OfflineOutboxSummary = {
  pending: number;
  needsReview: number;
  total: number;
  oldestAt: number | null;
};

export type OfflineReplayResult = {
  delivered: number;
  needsReview: number;
  remaining: number;
  authenticationRequired: boolean;
};

export type OfflineSyncBoundary = {
  schemaEpoch: number;
  resetToken: string | null;
};

export type LastKnownProjection<T> = {
  value: T;
  capturedAt: number;
  stale: true;
  readOnly: true;
};

export class OfflineStorageUnavailable extends Error {
  constructor() {
    super("offline_storage_unavailable");
    this.name = "OfflineStorageUnavailable";
  }
}

export class OfflineQueueFull extends Error {
  constructor() {
    super("offline_queue_full");
    this.name = "OfflineQueueFull";
  }
}

let databasePromise: Promise<IDBDatabase> | null = null;
const accountKeyPromises = new Map<string, Promise<CryptoKey>>();
const localAccountLocks = new Map<string, Promise<void>>();

function request<T>(operation: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    operation.onsuccess = () => resolve(operation.result);
    operation.onerror = () => reject(operation.error ?? new Error("indexeddb_request_failed"));
  });
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error("indexeddb_aborted"));
    transaction.onerror = () => reject(transaction.error ?? new Error("indexeddb_failed"));
  });
}

function openDatabase(): Promise<IDBDatabase> {
  if (!offlineStorageSupported()) return Promise.reject(new OfflineStorageUnavailable());
  if (databasePromise) return databasePromise;
  databasePromise = new Promise((resolve, reject) => {
    const operation = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    operation.onupgradeneeded = () => {
      const database = operation.result;
      if (!database.objectStoreNames.contains("keys")) {
        database.createObjectStore("keys", { keyPath: "accountId" });
      }
      if (!database.objectStoreNames.contains("outbox")) {
        const outbox = database.createObjectStore("outbox", { keyPath: "id" });
        outbox.createIndex("account", "accountId", { unique: false });
      }
      if (!database.objectStoreNames.contains("projections")) {
        const projections = database.createObjectStore("projections", { keyPath: "id" });
        projections.createIndex("account", "accountId", { unique: false });
      }
    };
    operation.onsuccess = () => {
      const database = operation.result;
      database.onversionchange = () => database.close();
      resolve(database);
    };
    operation.onerror = () => {
      databasePromise = null;
      reject(operation.error ?? new OfflineStorageUnavailable());
    };
    operation.onblocked = () => {
      databasePromise = null;
      reject(new OfflineStorageUnavailable());
    };
  });
  return databasePromise;
}

export function offlineStorageSupported(): boolean {
  return (
    typeof indexedDB !== "undefined" &&
    typeof crypto !== "undefined" &&
    (isNative() || Boolean(crypto.subtle)) &&
    typeof TextEncoder !== "undefined"
  );
}

function encode(value: string): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(value);
}

function ownedBytes(value: Uint8Array): Uint8Array<ArrayBuffer> {
  return new Uint8Array(value);
}

function additionalData(
  item: Pick<StoredCapture, "accountId" | "id" | "operation" | "schemaVersion">,
) {
  return encode(`${item.accountId}\n${item.id}\n${item.operation}\n${item.schemaVersion}`);
}

function projectionAdditionalData(
  item: Pick<StoredProjection, "accountId" | "id" | "name" | "schemaVersion">,
) {
  return encode(`${item.accountId}\n${item.id}\n${item.name}\n${item.schemaVersion}`);
}

async function accountKey(accountId: string): Promise<CryptoKey> {
  if (isNative()) throw new OfflineStorageUnavailable();
  const running = accountKeyPromises.get(accountId);
  if (running) return running;
  const created = (async () => {
    const database = await openDatabase();
    const read = database.transaction("keys", "readonly");
    const readComplete = transactionComplete(read);
    const existing = (await request(read.objectStore("keys").get(accountId))) as
      | StoredKey
      | undefined;
    await readComplete;
    if (existing?.key) return existing.key;

    const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, [
      "encrypt",
      "decrypt",
    ]);
    const write = database.transaction("keys", "readwrite");
    const writeComplete = transactionComplete(write);
    write.objectStore("keys").put({ accountId, key, createdAt: Date.now() } satisfies StoredKey);
    await writeComplete;
    return key;
  })();
  accountKeyPromises.set(accountId, created);
  try {
    return await created;
  } finally {
    accountKeyPromises.delete(accountId);
  }
}

async function markNativeAccount(accountId: string): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction("keys", "readwrite");
  const complete = transactionComplete(transaction);
  transaction.objectStore("keys").put({
    accountId,
    native: true,
    createdAt: Date.now(),
  } satisfies StoredKey);
  await complete;
}

async function encryptPayload(
  accountId: string,
  aad: Uint8Array,
  plaintext: Uint8Array,
): Promise<{ iv: ArrayBuffer; ciphertext: ArrayBuffer }> {
  if (isNative()) {
    const encrypted = await nativeEncryptOffline(accountId, aad, plaintext);
    await markNativeAccount(accountId);
    return encrypted;
  }
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: ownedBytes(aad) },
    await accountKey(accountId),
    ownedBytes(plaintext),
  );
  return { iv: iv.buffer, ciphertext };
}

async function decryptPayload(
  accountId: string,
  aad: Uint8Array,
  iv: ArrayBuffer,
  ciphertext: ArrayBuffer,
): Promise<ArrayBuffer> {
  if (isNative()) return nativeDecryptOffline(accountId, aad, iv, ciphertext);
  return crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(iv), additionalData: ownedBytes(aad) },
    await accountKey(accountId),
    ciphertext,
  );
}

async function capturesFor(accountId: string): Promise<StoredCapture[]> {
  const database = await openDatabase();
  const transaction = database.transaction("outbox", "readonly");
  const complete = transactionComplete(transaction);
  const rows = (await request(
    transaction.objectStore("outbox").index("account").getAll(accountId),
  )) as StoredCapture[];
  await complete;
  return rows.sort(
    (left, right) => left.createdAt - right.createdAt || left.id.localeCompare(right.id),
  );
}

async function putCapture(item: StoredCapture): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction("outbox", "readwrite");
  const complete = transactionComplete(transaction);
  transaction.objectStore("outbox").put(item);
  await complete;
  announceChange(item.accountId);
}

async function deleteCapture(accountId: string, id: string): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction("outbox", "readwrite");
  const complete = transactionComplete(transaction);
  const store = transaction.objectStore("outbox");
  const current = (await request(store.get(id))) as StoredCapture | undefined;
  if (current?.accountId === accountId) store.delete(id);
  await complete;
  announceChange(accountId);
}

function announceChange(accountId: string) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(OUTBOX_EVENT, { detail: { accountId } }));
  }
  if (typeof BroadcastChannel !== "undefined") {
    const channel = new BroadcastChannel(OUTBOX_EVENT);
    channel.postMessage({ accountId });
    channel.close();
  }
}

export function subscribeOfflineOutbox(accountId: string, callback: () => void): () => void {
  const onLocal = (event: Event) => {
    if ((event as CustomEvent<{ accountId?: string }>).detail?.accountId === accountId) callback();
  };
  window.addEventListener(OUTBOX_EVENT, onLocal);
  const channel =
    typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(OUTBOX_EVENT) : null;
  if (channel) {
    channel.onmessage = (event) => {
      if (event.data?.accountId === accountId) callback();
    };
  }
  return () => {
    window.removeEventListener(OUTBOX_EVENT, onLocal);
    channel?.close();
  };
}

async function queueOfflineCaptureUnlocked(
  accountId: string,
  draft: OfflineCaptureDraft,
): Promise<void> {
  const body = draft.body.trim();
  if (!body || body.length > 10_000) throw new TypeError("offline_capture_length_invalid");
  const existingRows = await capturesFor(accountId);
  const sameId = existingRows.find((item) => item.id === draft.id);
  if (sameId) {
    const existing = await decryptCapture(sameId);
    if (existing.body === body && existing.idempotencyKey === draft.idempotencyKey) return;
    throw new TypeError("offline_capture_id_reused");
  }
  const usedBytes = existingRows.reduce((total, item) => total + item.ciphertext.byteLength, 0);
  if (existingRows.length >= MAX_CAPTURE_ITEMS || usedBytes >= MAX_ENCRYPTED_BYTES) {
    throw new OfflineQueueFull();
  }

  const itemBase = {
    id: draft.id,
    accountId,
    operation: "memory.capture.create" as const,
    schemaVersion: CAPTURE_SCHEMA_VERSION as 1,
  };
  const plaintext = encode(
    JSON.stringify({ id: draft.id, body, idempotencyKey: draft.idempotencyKey }),
  );
  const { iv, ciphertext } = await encryptPayload(accountId, additionalData(itemBase), plaintext);
  if (usedBytes + ciphertext.byteLength > MAX_ENCRYPTED_BYTES) throw new OfflineQueueFull();
  await putCapture({
    ...itemBase,
    idempotencyKey: draft.idempotencyKey,
    iv,
    ciphertext,
    createdAt: Date.now(),
    state: "pending",
    attempts: 0,
    lastErrorCode: null,
  });
}

async function withLocalAccountLock<T>(accountId: string, operation: () => Promise<T>): Promise<T> {
  const previous = localAccountLocks.get(accountId) ?? Promise.resolve();
  let release!: () => void;
  const turn = new Promise<void>((resolve) => {
    release = resolve;
  });
  const tail = previous.catch(() => undefined).then(() => turn);
  localAccountLocks.set(accountId, tail);
  await previous.catch(() => undefined);
  try {
    return await operation();
  } finally {
    release();
    if (localAccountLocks.get(accountId) === tail) localAccountLocks.delete(accountId);
  }
}

type LockManagerLike = {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
};

function withAccountLock<T>(accountId: string, operation: () => Promise<T>): Promise<T> {
  const locks = (
    typeof navigator !== "undefined" ? (navigator as unknown as { locks?: LockManagerLike }) : {}
  ).locks;
  if (locks) {
    return locks.request(`exognitos-outbox:${accountId}`, operation);
  }
  return withLocalAccountLock(accountId, operation);
}

export function queueOfflineCapture(accountId: string, draft: OfflineCaptureDraft): Promise<void> {
  return withAccountLock(accountId, () => queueOfflineCaptureUnlocked(accountId, draft));
}

async function decryptCapture(item: StoredCapture): Promise<OfflineCaptureDraft> {
  const plaintext = await decryptPayload(
    item.accountId,
    additionalData(item),
    item.iv,
    item.ciphertext,
  );
  const value = JSON.parse(new TextDecoder().decode(plaintext)) as Partial<OfflineCaptureDraft>;
  if (
    value.id !== item.id ||
    value.idempotencyKey !== item.idempotencyKey ||
    typeof value.body !== "string" ||
    value.body.length < 1 ||
    value.body.length > 10_000
  ) {
    throw new DOMException("offline_payload_integrity_failed", "DataError");
  }
  return { id: value.id, body: value.body, idempotencyKey: value.idempotencyKey };
}

async function sendCapture(draft: OfflineCaptureDraft): Promise<void> {
  await unwrap(
    api.POST("/api/v4/memory/capture", {
      body: { id: draft.id, body: draft.body },
      headers: { "Idempotency-Key": draft.idempotencyKey },
    }),
  );
}

type CaptureSender = (draft: OfflineCaptureDraft) => Promise<void>;

async function replayUnlocked(
  accountId: string,
  sender: CaptureSender,
): Promise<OfflineReplayResult> {
  const rows = (await capturesFor(accountId))
    .filter((item) => item.state === "pending")
    .slice(0, 25);
  let delivered = 0;
  let authenticationRequired = false;
  for (const row of rows) {
    let draft: OfflineCaptureDraft;
    try {
      draft = await decryptCapture(row);
    } catch {
      await putCapture({
        ...row,
        state: "corrupted",
        attempts: row.attempts + 1,
        lastErrorCode: "ciphertext_invalid",
      });
      continue;
    }
    try {
      await sender(draft);
      await deleteCapture(accountId, row.id);
      delivered += 1;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        authenticationRequired = true;
        break;
      }
      if (error instanceof ApiError && (error.status === 409 || error.status === 422)) {
        await putCapture({
          ...row,
          state: error.status === 409 ? "conflict" : "rejected",
          attempts: row.attempts + 1,
          lastErrorCode: error.code,
        });
        continue;
      }
      // Network and server failures keep the exact logical replay pending. A
      // later online event retries with the same UUID and idempotency key.
      await putCapture({
        ...row,
        attempts: row.attempts + 1,
        lastErrorCode: error instanceof ApiError ? error.code : "transport_unavailable",
      });
      break;
    }
  }
  const summary = await offlineOutboxSummary(accountId);
  return {
    delivered,
    needsReview: summary.needsReview,
    remaining: summary.total,
    authenticationRequired,
  };
}

export async function replayOfflineCaptures(
  accountId: string,
  sender: CaptureSender = sendCapture,
): Promise<OfflineReplayResult> {
  try {
    const result = await withAccountLock(accountId, () => replayUnlocked(accountId, sender));
    if (result.delivered > 0) observeFrontendMetric("sync_replay_success", 1);
    if (result.needsReview > 0) observeFrontendMetric("sync_replay_review", 1);
    if (result.authenticationRequired) observeFrontendMetric("sync_replay_auth", 1);
    if (result.remaining > 0) observeFrontendMetric("sync_replay_pending", 1);
    return result;
  } catch (error) {
    // Count the closed failure class only. The thrown object may contain a URL,
    // server response, authored payload, or local database details.
    observeFrontendMetric("sync_replay_failure", 1);
    throw error;
  }
}

export async function offlineOutboxSummary(accountId: string): Promise<OfflineOutboxSummary> {
  const rows = await capturesFor(accountId);
  const needsReview = rows.filter((item) => item.state !== "pending").length;
  return {
    pending: rows.length - needsReview,
    needsReview,
    total: rows.length,
    oldestAt: rows[0]?.createdAt ?? null,
  };
}

export async function listOfflineDraftsForReview(accountId: string): Promise<OfflineDraftReview[]> {
  const rows = await capturesFor(accountId);
  return Promise.all(
    rows.map(async (row) => {
      let body: string | null = null;
      try {
        body = (await decryptCapture(row)).body;
      } catch {
        // Damaged ciphertext is intentionally not guessed or partially shown.
      }
      return {
        id: row.id,
        body,
        createdAt: row.createdAt,
        state: row.state,
        errorCode: row.lastErrorCode,
      };
    }),
  );
}

export async function retryOfflineDraft(accountId: string, id: string): Promise<void> {
  const row = (await capturesFor(accountId)).find((candidate) => candidate.id === id);
  if (!row) return;
  await putCapture({ ...row, state: "pending", lastErrorCode: null });
}

export function discardOfflineDraft(accountId: string, id: string): Promise<void> {
  return deleteCapture(accountId, id);
}

function deletePartitionRows(store: IDBObjectStore, accountId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cursor = store.index("account").openKeyCursor(IDBKeyRange.only(accountId));
    cursor.onerror = () => reject(cursor.error ?? new Error("indexeddb_cursor_failed"));
    cursor.onsuccess = () => {
      const current = cursor.result;
      if (!current) {
        resolve();
        return;
      }
      store.delete(current.primaryKey);
      current.continue();
    };
  });
}

export async function clearOfflineAccount(accountId: string): Promise<void> {
  if (!offlineStorageSupported()) return;
  if (isNative()) {
    // Local ciphertext is still removed if an already-reset OS vault reports
    // a missing key; retaining unreadable private bytes serves no purpose.
    await nativeClearOfflineKey(accountId).catch(() => undefined);
  }
  const database = await openDatabase();
  const transaction = database.transaction(["keys", "outbox", "projections"], "readwrite");
  const complete = transactionComplete(transaction);
  transaction.objectStore("keys").delete(accountId);
  await Promise.all([
    deletePartitionRows(transaction.objectStore("outbox"), accountId),
    deletePartitionRows(transaction.objectStore("projections"), accountId),
  ]);
  await complete;
  accountKeyPromises.delete(accountId);
  announceChange(accountId);
}

export async function activateOfflineAccount(accountId: string): Promise<void> {
  if (!offlineStorageSupported()) return;
  const database = await openDatabase();
  const transaction = database.transaction("keys", "readonly");
  const complete = transactionComplete(transaction);
  const accountIds = (await request(transaction.objectStore("keys").getAllKeys())).map(String);
  await complete;
  for (const staleAccountId of accountIds) {
    if (staleAccountId === accountId) continue;
    await clearOfflineAccount(staleAccountId);
  }
}

export async function storeLastKnownProjection<T>(
  accountId: string,
  name: "inbox_count",
  boundary: OfflineSyncBoundary,
  value: T,
): Promise<void> {
  if (!ALLOWED_PROJECTIONS.has(name)) throw new TypeError("offline_projection_not_allowed");
  const id = `${accountId}:${name}`;
  const base = { id, accountId, name, schemaVersion: 1 as const };
  const { iv, ciphertext } = await encryptPayload(
    accountId,
    projectionAdditionalData(base),
    encode(JSON.stringify(value)),
  );
  const database = await openDatabase();
  const transaction = database.transaction("projections", "readwrite");
  const complete = transactionComplete(transaction);
  transaction.objectStore("projections").put({
    ...base,
    schemaEpoch: boundary.schemaEpoch,
    resetToken: boundary.resetToken,
    capturedAt: Date.now(),
    iv,
    ciphertext,
  } satisfies StoredProjection);
  await complete;
}

export async function readLastKnownProjection<T>(
  accountId: string,
  name: "inbox_count",
  boundary?: OfflineSyncBoundary,
): Promise<LastKnownProjection<T> | null> {
  if (!ALLOWED_PROJECTIONS.has(name)) return null;
  const database = await openDatabase();
  const transaction = database.transaction("projections", "readonly");
  const complete = transactionComplete(transaction);
  const row = (await request(transaction.objectStore("projections").get(`${accountId}:${name}`))) as
    | StoredProjection
    | undefined;
  await complete;
  if (
    !row ||
    row.accountId !== accountId ||
    (boundary !== undefined &&
      (row.schemaEpoch !== boundary.schemaEpoch || row.resetToken !== boundary.resetToken))
  ) {
    return null;
  }
  try {
    const plaintext = await decryptPayload(
      accountId,
      projectionAdditionalData(row),
      row.iv,
      row.ciphertext,
    );
    return {
      value: JSON.parse(new TextDecoder().decode(plaintext)) as T,
      capturedAt: row.capturedAt,
      stale: true,
      readOnly: true,
    };
  } catch {
    return null;
  }
}
